import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useChatSession } from "@/features/chat/api/use-session";
import { chatKey } from "@/features/chat/api/chat-query";
import { useWorkspaceRealtime } from "@/features/chat/hooks/use-workspace-realtime";
import type { Identity } from "@/features/chat/types";

/** Reuse webchat's authenticated Relay, reconnect, and session keepalive. */
export function HandlerRealtime({
  chatId,
  onUpdate,
  onConnected,
}: {
  chatId: string;
  onUpdate: () => void;
  onConnected: (connected: boolean) => void;
}) {
  const session = useChatSession();
  return session.data?.identity ? (
    <HandlerRoom
      identity={session.data.identity}
      chatId={chatId}
      onUpdate={onUpdate}
      onConnected={onConnected}
    />
  ) : null;
}

function HandlerRoom({
  identity,
  chatId,
  onUpdate,
  onConnected,
}: {
  identity: Identity;
  chatId: string;
  onUpdate: () => void;
  onConnected: (connected: boolean) => void;
}) {
  const client = useQueryClient();
  const realtime = useWorkspaceRealtime(identity, chatId, true);
  const callbacks = useRef({ onUpdate, onConnected });
  useEffect(() => {
    callbacks.current = { onUpdate, onConnected };
  }, [onUpdate, onConnected]);
  useEffect(() => {
    callbacks.current.onConnected(realtime.connection === "Connected");
    return () => callbacks.current.onConnected(false);
  }, [realtime.connection]);
  useEffect(() => {
    const key = JSON.stringify(chatKey(identity, chatId));
    return client.getQueryCache().subscribe((event) => {
      if (
        event.type === "updated" &&
        event.action.type === "success" &&
        JSON.stringify(event.query.queryKey) === key
      )
        callbacks.current.onUpdate();
    });
  }, [client, identity, chatId]);
  return null;
}
